import { DateTime } from 'luxon';

/** A budget for events reported to Sentry by hand. */
export type TSentryEventThrottleOptions = {
  /** Same signature is reported at most once within this window. */
  cooldownMs: number;
  /** Hard cap on the number of events reported per page session. */
  maxPerSession: number;
};

export type TSentryEventThrottle = {
  /**
   * `true` when this occurrence is the one to send — and it counts against the
   * budget from that moment. Call it last, after every filter that would drop
   * the event anyway, so a dropped event never consumes a slot or starts a
   * cooldown for a signature that was never reported.
   */
  shouldReport(signature: string): boolean;
  /** Clears the budget. Exists for specs; nothing in the app calls it. */
  reset(): void;
};

/**
 * Deterministic sampling for the events this app reports to Sentry by hand
 * (`captureException` calls of our own, outside the SDK's instrumentation).
 *
 * Two consumers, two very different streams — runtime errors caught by the
 * Angular `ErrorHandler`, and failed requests caught by
 * `SentryHttpErrorInterceptor` — with the same budget problem: a broken render
 * or a backend that is down fires the *same* event dozens of times per second,
 * in every open tab, and the interesting part (that it happened, where, to
 * whom) is already in the first one.
 *
 * The limit is per **signature**, never random. Sentry's own `sampleRate` keeps
 * a percentage of everything, which drops the single occurrence of a rare bug
 * as readily as the 400th copy of a loud one; a cooldown keeps the first of
 * every distinct signature and discards only the repeats. What the caller puts
 * in the signature is what decides "distinct" — see each consumer.
 *
 * The session cap is the safety net for the case the cooldown cannot cover: a
 * screen looping through *many* distinct signatures. It is one-way on purpose —
 * once spent, the page stays quiet until it is reloaded.
 *
 * Memory is bounded by `maxPerSession`: only an accepted report writes to the
 * map, and acceptance stops at the cap.
 */
export function createSentryEventThrottle({
  cooldownMs,
  maxPerSession,
}: TSentryEventThrottleOptions): TSentryEventThrottle {
  const lastSentBySignature = new Map<string, number>();
  let sessionEventCount = 0;

  return {
    shouldReport(signature: string): boolean {
      if (sessionEventCount >= maxPerSession) return false;

      const now = DateTime.now().toMillis();
      const lastSent = lastSentBySignature.get(signature);
      if (lastSent !== undefined && now - lastSent < cooldownMs) return false;

      lastSentBySignature.set(signature, now);
      sessionEventCount += 1;
      return true;
    },

    reset(): void {
      lastSentBySignature.clear();
      sessionEventCount = 0;
    },
  };
}
